import { faPlus, faTrash } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { type ReactElement, useMemo } from 'react'
import { type Control, useFieldArray, useWatch } from 'react-hook-form'
import { formatAmount } from '~/common/utils'
import { Dropdown, TextInput } from '~/components/Form'
import type { InputInFormProps } from '~/components/Form/Input/input'
import {
  Button,
  ButtonVariation
} from '~/components/Global/Interactions/Button'
import { useTaxonomy } from '~/domains/taxonomies/queries/useTaxonomy'
import type { InvoiceLineData } from '../types'
import { computeLineTotalHT } from '../utils/invoiceTotals'
import {
  addLineButton,
  deleteLineButton,
  dropDownValueBox,
  inputField,
  invoiceLinesSection,
  lineFieldsRow,
  lineFieldsRowHT,
  linesHeader,
  lineTotal
} from './InvoiceModal.css'

type InvoiceForm = {
  lines: InvoiceLineData[]
}

type InvoiceLinesFieldArrayProps = Omit<InputInFormProps, 'control'> & {
  control?: Control<InvoiceForm>
  name?: string
  isEditable: boolean
}

export const InvoiceLinesFieldArray = ({
  control,
  register,
  watch,
  setValue,
  isEditable
}: InvoiceLinesFieldArrayProps): ReactElement => {
  const { data: vatRates = [] } = useTaxonomy('vat_rates')
  const effectiveControl = control as Control<any>
  const effectiveRegister = register
  const effectiveWatch = watch
  const effectiveSetValue = setValue

  const { fields, append, remove } = useFieldArray<InvoiceForm>({
    control: effectiveControl,
    name: 'lines'
  })

  const handleAddLine = () => {
    const lastField = fields.at(-1)
    const lastVatRateId =
      lastField?.vatRateId ??
      (vatRates.length > 0 ? String(vatRates[0]?.id) : '')

    append({
      description: '',
      quantity: 1,
      unitPriceHT: 0,
      vatRateId: lastVatRateId
    })
  }

  const handleRemoveLine = (index: number) => {
    if (fields.length > 1) {
      remove(index)
    }
  }

  return (
    <div className={invoiceLinesSection}>
      <div className={linesHeader}>
        <div>Description</div>
        <div>Quantité</div>
        <div>Prix unitaire HT</div>
        <div>Taux de TVA</div>
        <div className={lineFieldsRowHT}>Total HT</div>
        {isEditable && fields.length > 1 && <div />}
      </div>

      {fields.map((field, index) => (
        <div key={field.id} className={lineFieldsRow}>
          <TextInput
            wrapper={false}
            type="text"
            noWrapperClassName={inputField}
            name={`lines.${index}.description`}
            placeholder="Description"
            required
            disabled={!isEditable}
            control={effectiveControl}
            register={effectiveRegister}
          />

          <TextInput
            wrapper={false}
            noWrapperClassName={inputField}
            name={`lines.${index}.quantity`}
            type="number"
            step="1"
            placeholder="1"
            required
            disabled={!isEditable}
            control={effectiveControl}
            register={effectiveRegister}
          />

          <TextInput
            wrapper={false}
            noWrapperClassName={inputField}
            name={`lines.${index}.unitPriceHT`}
            type="number"
            step="0.01"
            placeholder="0.00"
            required
            disabled={!isEditable}
            control={effectiveControl}
            register={effectiveRegister}
          />

          <Dropdown
            name={`lines.${index}.vatRateId`}
            placeholder="Taux de TVA"
            options={vatRates.map((vr) => ({
              label: `${vr.name} (${(vr as any).rate ?? 0}%)`,
              value: String(vr.id)
            }))}
            required
            disabled={!isEditable}
            control={effectiveControl}
            register={effectiveRegister}
            watch={effectiveWatch}
            setValue={effectiveSetValue}
            valueBoxClassName={dropDownValueBox}
          />

          <InvoiceLineTotal
            field={field}
            lineIndex={index}
            control={effectiveControl}
          />

          {isEditable && fields.length > 1 && (
            <Button
              variation={ButtonVariation.Danger}
              onClick={() => handleRemoveLine(index)}
              title="Supprimer cette ligne"
              className={deleteLineButton}
            >
              <FontAwesomeIcon icon={faTrash} />
            </Button>
          )}
        </div>
      ))}

      {isEditable && (
        <Button
          variation={ButtonVariation.Primary}
          onClick={handleAddLine}
          title="Ajouter une ligne"
          className={addLineButton}
          fullWidth
        >
          <FontAwesomeIcon icon={faPlus} /> Ajouter une ligne
        </Button>
      )}
    </div>
  )
}

const InvoiceLineTotal = ({
  field,
  lineIndex,
  control
}: {
  field: any
  lineIndex: number
  control?: Control<InvoiceForm>
}): ReactElement => {
  const watchedLine = useWatch({ control, name: `lines.${lineIndex}` }) as
    | InvoiceLineData
    | undefined

  const line: InvoiceLineData | undefined =
    watchedLine ?? (field as InvoiceLineData)

  const totalHT = useMemo(() => {
    if (!line) return 0
    return computeLineTotalHT({
      quantity: Number(line.quantity) || 0,
      unitPriceHT: Number(line.unitPriceHT) || 0
    })
  }, [line])

  return (
    <div className={lineTotal}>
      <strong>{formatAmount(totalHT)} €</strong>
    </div>
  )
}
